import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick, getCurrentInstance } from 'vue'
import { MissionPlanUiState, buildMissionPlanStateId } from '@/models/ui/MissionPlanUiState'
import FleetGliderAssociation from '@/components/fleet/FleetGliderAssociation.vue'
import deepEqual from 'deep-equal'
import FleetCoverageParameter from '@/components/fleet/FleetCoverageParameter.vue'
import store from '@/store'
import { AloDbFleet, AloDbFleetCoverage } from '@/models/alodb/AloDbFleet'
import { buildFleetUIStateId } from '@/models/ui/FleetUiState'
import { AloDbMissionGlobalCoverage, isAloDbMissionGlobalCoverage } from '@/models/alodb/AloDbMissionGlobal'
import { CoverageStep } from '@/enum/CoverageStep'
import { CoverageState } from '@/enum/CoverageState'
import { AloDbCoverageIteration } from '@/models/alodb/AloDbCoverageIteration'
import { cloneDeep } from 'lodash'
import { i18n } from '@/locale/vueI18n'
import { VuetifyConfirmObject } from 'vuetify-confirm/types'
import { getDefaultGliderCoverageConfig, IGliderCoverageConfig } from '@/models/iris/smartMission/IGliderCoverageConfig'
import { getDefaultGliderConfig } from '@/models/iris/smartMission/IGliderConfig'
import { extractDistinctProp } from '@/utils/groupBy'
import { ISmartSimulationCoverage } from '@/models/iris/smartMission/ISmartSimulationCoverage'
import { isReadyForIrisFleetCoverage } from '@/models/iris/fleet/IFleetIrisConfigCoverage'
import { DepRecoMode } from '@/enum/DepRecoMode'
import {
  getDefaultGliderFleetCoverageConfig,
  IGliderFleetCoverageConfig,
} from '@/models/iris/fleet/IGliderFleetCoverageConfig'


export default /*#__PURE__*/_defineComponent({
  __name: 'FleetDetailParameterCoverage',
  props: {
    isleft: { type: Boolean },
    uiid: null,
    isVisible: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  isleft: boolean
  uiid: string
  isVisible: boolean
};



// Accès aux plugins Vue via l'instance (Vue 2 + Composition API)
const instance = getCurrentInstance()
const $confirm = instance?.proxy?.$confirm!

const templateId = ref(null)

const gliderAssociation = ref(null)

store.myActions.MISSIONVIRTUAL_ENSURE_LIST()
store.myActions.COVERAGEITERATION_ENSURE_LIST()
store.myActions.MISSIONGLOBAL_ENSURE_LIST()

const visibleTab = ref('configuration') // configuration || association

const isConfigurationTabVisible = computed(() => visibleTab.value === 'configuration')
const isAssociationTabVisible = computed(() => visibleTab.value === 'association')

const modified = computed(() => store.myGetters.modifiedAdmAdminFleet as AloDbFleetCoverage)
const original = computed(() => store.myGetters.originalAdmAdminFleet as AloDbFleetCoverage)

const map_uiid = computed(() => buildFleetUIStateId(original.value.FLE_ID!, 'map'))

const btnActif = computed(() => !deepEqual(original.value, modified.value) /*&& !editCoverageState.value!.saving*/)

const selectedFilterEnsIds = computed(() => {
  //@ts-ignore
  return store.myGetters.filterMission?.ensIds?.at(0) ?? undefined
})

const missionGlobals = computed(() => {
  return store.myGetters.missionGlobals.filter(
    m =>
      isAloDbMissionGlobalCoverage(m) &&
      //@ts-ignore
      ((!store.myGetters.isOperator && !store.myGetters.isSysAdmin) ||
        selectedFilterEnsIds.value == undefined ||
        m.ENS_ID == selectedFilterEnsIds.value),
  ) as Array<AloDbMissionGlobalCoverage>
})

const endedCoverageIterations = computed(() => {
  return store.myGetters.coverageIterations.filter(
    ci =>
      ci.COV_PROCESSING_STEP == CoverageStep.FinalSimulation &&
      (ci.COV_PROCESSING_STATE == CoverageState.aborted || ci.COV_PROCESSING_STATE == CoverageState.finished),
  )
})

const maxEndedCoverageIterationIdByMigId = computed(() => {
  return endedCoverageIterations.value.reduce((acc, it) => {
    if (!acc[it.MIG_ID!] || acc[it.MIG_ID!] < it.COV_ID!) acc[it.MIG_ID!] = it.COV_ID!
    return acc
  }, {} as { [key: number]: number })
})

const covIterationsTemplates = computed(() => {
  return missionGlobals.value.reduce((list, m) => {
    const maxCovId = maxEndedCoverageIterationIdByMigId.value[m.MIG_ID!]
    const covIteration =
      store.myGetters.coverageIterationsById[(m.config?.templateSimId ?? 0) > 0 ? m.config!.templateSimId! : maxCovId]
    if (covIteration?.resultMipJson) {
      list.push(covIteration)
    }
    return list
  }, [] as Array<AloDbCoverageIteration>)
})

function loadFromTemplate() {
  if (templateId.value) {
    const cov = store.myGetters.coverageIterationsById[templateId.value]
    const conf = cov.config as any as ISmartSimulationCoverage

    modified.value.FLE_IRIS_RESULT_GRID_JSON = cov.COV_IRIS_RESULT_GRID_JSON
    modified.value.resultGridJson = cov.resultGridJson

    modified.value.config.common_recovery = conf.recovery_mode == DepRecoMode.common
    modified.value.config.recovery_point = conf.recovery_point

    modified.value.config.smartplanning_template = {
      processing_company: cov.COV_COMPANY!,
      processing_iteration: cov.COV_ITERATION!,
      processing_name: store.myGetters.missionGlobalsById[cov.MIG_ID!].MIG_KEY!,
    }

    modified.value.config.gliders_config = conf.gliders_config.reduce((acc, gliConf, idx) => {
      acc[gliConf.libelle! ?? `GLIDER${idx + 1}`] = gliConf
      return acc
    }, {} as Record<string, IGliderCoverageConfig>)
    modified.value.config.glider_number = conf.gliders_config.length

    AloDbFleet.correctGliderAssociation(modified.value)

    modified.value.param.cov_id = cov.COV_ID!

    templateId.value = null
  }
}

function showConfigurationTab() {
  visibleTab.value = 'configuration'
}

function showAssociationTab() {
  visibleTab.value = 'association'
}

function bestFit() {
  store.myActions.UI_REFRESH_BEST_FIT_MAP(map_uiid.value)
}

async function restoreConfig() {
  await store.myActions.ADMINFLEET_CANCEL_MODIFIED_ADM()
  if (gliderAssociation.value) {
    //@ts-ignore
    gliderAssociation.value.genereAssociation()
  }
}

async function saveConfig() {
  //la flotte est lancé
  if (original.value.param.start_date) {
    const errors = isReadyForIrisFleetCoverage(modified.value.config)
    if (!errors?.length) {
      generateGliderConfig()
      await store.myActions.ADMINFLEET_SAVE_REQUEST()
      await store.myActions.ADMINFLEET_PUBLISH_IRIS_REQUEST(modified.value.config)
    } else {
      $confirm(`<ul><li>${errors.map(e => i18n.t(`Fleet.Check.${e}`)).join('</li><li>')}</li></ul>`, {
        title: i18n.t('Simulation.Check.Title'),
        buttonTrueText: 'Ok',
        buttonFalseText: '',
      } as VuetifyConfirmObject)
    }
  } else {
    await store.myActions.ADMINFLEET_SAVE_REQUEST()
  }
}

function generateGliderConfig() {
  modified.value.config.gliders_config = Object.keys(modified.value.config.glider_association).reduce(
    (gliConf, key) => {
      const association = modified.value.config.glider_association[key]
      const gliKey = association.at(0) as string
      const misIdx = association.at(1)
      const mission = store.myGetters.missions.find(m => m.gliKey?.trim() == gliKey.trim() && m.misIdx == misIdx)

      if (mission) {
        console.log('generateGliderConfig', mission.misId)
        const capteur = extractDistinctProp(mission!.mesureTypeExArray, m => m?.UMT_KEY ?? '').map((p, idx) => ({
          id: idx + 1,
          snr_label: p,
        }))
        const lastYo = mission.yoArray?.at(-1)
        gliConf[gliKey] = getDefaultGliderFleetCoverageConfig({
          libelle: gliKey,
          payload: capteur ?? [],
          start_energy: lastYo?.batteries ?? undefined,
          prf_nav: modified.value.config.gliders_config[gliKey]?.prf_nav ??
            modified.value.config.gliders_config[key]?.prf_nav ?? [MissionPlanUiState.getDefaultProfil()],
        })
      }
      return gliConf
    },
    {} as Record<string, IGliderFleetCoverageConfig>,
  )
  console.log('generateGliderConfig', modified.value.config)
  modified.value.config.glider_number = Object.keys(modified.value.config.gliders_config).length
}

async function startFleet() {
  const errors = isReadyForIrisFleetCoverage(modified.value.config)
  if (!errors?.length) {
    modified.value.param.start_date = new Date().toISOString()
    modified.value.config.fleet_name = modified.value.FLE_NAME!
    modified.value.config.fleet_company = modified.value.FLE_COMPANY!
    generateGliderConfig()

    await saveConfig()

    await store.myActions.ADMINFLEET_PUBLISH_IRIS_REQUEST(modified.value.config)
  } else {
    $confirm(`<ul><li>${errors.map(e => i18n.t(`Fleet.Check.${e}`)).join('</li><li>')}</li></ul>`, {
      title: i18n.t('Simulation.Check.Title'),
      buttonTrueText: 'Ok',
      buttonFalseText: '',
    } as VuetifyConfirmObject)
    await saveConfig()
  }
}

return { __sfc: true,props, instance, $confirm, templateId, gliderAssociation, visibleTab, isConfigurationTabVisible, isAssociationTabVisible, modified, original, map_uiid, btnActif, selectedFilterEnsIds, missionGlobals, endedCoverageIterations, maxEndedCoverageIterationIdByMigId, covIterationsTemplates, loadFromTemplate, showConfigurationTab, showAssociationTab, bestFit, restoreConfig, saveConfig, generateGliderConfig, startFleet, FleetGliderAssociation, FleetCoverageParameter }
}

})